import { useReducer, useState } from 'react'
import {
    MenuItem,
    TextField,
    FormControl,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Checkbox,
    Button,
    FormHelperText,
    Link
} from '@mui/material'
import { isEmailValid, states } from '../../services/helper'
import { makeStyles } from '@mui/styles'
import { sendForm } from '../../services/client'
import Alert from '../Alert/Alert'

const useStyles = makeStyles((theme) => ({
    form: {
        borderRadius: 10,
        border: '1px solid #dadce0',
        padding: 30
    },
    formTitle: {
        fontSize: 28,
        textAlign: 'left',
        paddingLeft: 10
    },
    formControl: {
        width: '100%',
        margin: '20px 0 0 !important',
    },
    label: {
        display: 'flex'
    },
    selectEmpty: {
        marginTop: 20,
    },
    option: {
        textAlign: 'left'
    },
    radioButton: {
        marginLeft: 0
    },
    terms: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: 20,
        marginLeft: 0,
        paddingLeft: 0,
        marginBottom: 10
    },
    submitButton: {
        marginTop: 30,
        border: '5px solid transparent',
        transition: '0.5s',
        display: 'block',
        minWidth: 200,
        margin: '0 auto',
        padding: '5px 0px',
        backgroundColor: '#940E15',
        fontWeight: 300,
        fontSize: 14,
        letterSpacing: 1,
        color: '#FFF',
        textAlign: 'center',
        textDecoration: 'none',
        boxSizing: 'border-box',
        '&:disabled': {
            background: '#cfcfcf !important',
        },
        borderRadius: 50
    },
    required: {
        fontSize: 16,
        color: '#940E15'
    }
}))

export type DebateFormData = {
    first_name: string;
    last_name: string;
    email: string;
    city: string;
    state: string;
    political_affiliation: string;
    agreed_to_terms: string;
}

const defaultData = {
    first_name: '',
    last_name: '',
    email: '',
    city: '',
    state: '',
    political_affiliation: '',
    agreed_to_terms: ''
}

export default function DebateForm() {
    const classes = useStyles()

    const [state, setState] = useState({
        submitting: false,
        submitted: false,
        error: false
    })

    const [formErrors, setFormErrors] = useState({
        first_name: false,
        last_name: false,
        email: false,
        city: false,
        state: false,
        political_affiliation: false,
        agreed_to_terms: false
    })

    const [data, setData] = useReducer(
        (state: any, newState: any) => ({ ...state, ...newState }), {}
    )

    const setErrors = (field: string, validation = '') => {
        setFormErrors(current => ({ ...current, [field]: !data[field] }))
    }

    const formDisabled = !data.first_name || !data.last_name || !data.email
        || !isEmailValid(data.email) || !data.city || !data.state || !data.political_affiliation || !data.agreed_to_terms

    let emailValidationText
    if (formErrors.email) emailValidationText = 'Email Address is required'
    else if (data.email && !isEmailValid(data.email)) emailValidationText = 'Please enter a valid Email Address'
    else emailValidationText = ''

    const submitForm = async () => {
        setState({ submitting: true, submitted: false, error: false })
        try {
            await sendForm('debate', data)
            setState({ submitting: false, submitted: true, error: false })
            setData(defaultData)
        } catch (err) {
            setState({ submitting: false, submitted: false, error: true })
        }
    }

    return (
        <form className={classes.form}>
            <h3 className={classes.formTitle}>Debater Sign-Up <span className={classes.required}>(note: all fields are required)</span></h3>
            <FormControl className={classes.formControl}>
                <TextField
                    label={'First Name'}
                    value={data.first_name}
                    onBlur={() => setErrors('first_name')}
                    onChange={e => setData({ first_name: e.target.value })}
                    fullWidth
                    required
                    error={formErrors.first_name}
                    helperText={formErrors.first_name && 'First Name is required'}
                    variant="standard"
                />
            </FormControl>
            <FormControl className={classes.formControl}>
                <TextField
                    label={'Last Name'}
                    value={data.last_name}
                    onBlur={() => setErrors('last_name')}
                    onChange={e => setData({ last_name: e.target.value })}
                    fullWidth
                    required
                    error={formErrors.last_name}
                    helperText={formErrors.last_name && 'Last Name is required'}
                    variant="standard"
                />
            </FormControl>
            <FormControl className={classes.formControl}>
                <TextField
                    type="email"
                    label={'Email Address'}
                    value={data.email}
                    onBlur={() => setErrors('email')}
                    onChange={e => setData({ email: e.target.value })}
                    fullWidth
                    required
                    error={formErrors.email || (!!data.email && !isEmailValid(data.email))}
                    helperText={emailValidationText}
                    variant="standard"
                />
            </FormControl>
            <FormControl className={classes.formControl}>
                <TextField
                    label={'City'}
                    value={data.city}
                    onBlur={() => setErrors('city')}
                    onChange={e => setData({ city: e.target.value })}
                    fullWidth
                    required
                    error={formErrors.city}
                    helperText={formErrors.city && 'City is required'}
                    variant="standard"
                />
            </FormControl>
            <FormControl className={classes.formControl} error={formErrors.state}>
                <TextField
                    label={'State'}
                    select
                    id="demo-simple-select-standard"
                    value={data.state || ''}
                    fullWidth
                    required
                    error={formErrors.state}
                    onChange={e => setData({ state: e.target.value })}
                    variant="standard"
                >
                    <MenuItem value="">
                        <em>Choose state</em>
                    </MenuItem>
                    {states.map(state => <MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>)}
                </TextField>
                {formErrors.state && <FormHelperText>State is required</FormHelperText>}
            </FormControl>
            <FormControl className={classes.formControl} component="fieldset" error={formErrors.political_affiliation}>
                <FormLabel required component="legend">Political Affiliation</FormLabel>
                <RadioGroup aria-label="political" name="gender1" value={data.political_affiliation} onChange={e => setData({ political_affiliation: e.target.value })}>
                    <FormControlLabel className={classes.radioButton} value="Democrat" control={<Radio />} label="Democrat" />
                    <FormControlLabel className={classes.radioButton} value="Republican" control={<Radio />} label="Republican" />
                    <FormControlLabel className={classes.radioButton} value="Other" control={<Radio />} label="Other" />
                </RadioGroup>
                {formErrors.political_affiliation && <FormHelperText>Political Affiliation is required</FormHelperText>}
            </FormControl>
            <FormControl className={classes.formControl} error={formErrors.agreed_to_terms}>
                <h3 className={classes.terms}>Terms And Conditions</h3>
                <FormControlLabel
                    value="agreed_to_terms"
                    control={<Checkbox color="primary" value={data.agreed_to_terms} onBlur={() => setErrors('agreed_to_terms')} onChange={e => setData({ agreed_to_terms: e.target.checked })} />}
                    label={<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}><span>I agree with the <Link href="https://ocov.us/docs/OCOV_Debate_Participation_Agreement.pdf" target="_blank" rel="noopener">Terms and Conditions</Link> </span> </div>}
                    labelPlacement="end"
                    onBlur={() => setErrors('agreed_to_terms')}
                />
                {formErrors.agreed_to_terms && !data.agreed_to_terms && <FormHelperText>You must agree to the terms</FormHelperText>}
            </FormControl>
            <FormControl className={classes.formControl}>
                <Button className={classes.submitButton} disabled={formDisabled || state.submitting} aria-label={'Submit Debate Form'} onClick={submitForm}>Sign-Up</Button>
            </FormControl>
            <Alert title={state.submitted ? 'Submission Received!' : 'An Error Has Occurred'} message={state.submitted ? 'Thank you for your interest in participating in OCOV debates. We will contact shortly with the next steps.' : `We've encountered an unexpected error while attempted to submit the form. Please try again later.`} open={state.submitted || state.error} handleClose={() => setState({ submitting: false, submitted: false, error: false })} />
        </form>
    )
}